import React, { useState, useEffect } from "react";
import { apiService } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Loader2, ArrowLeftRight, RefreshCw, Check, X, 
  ArrowRight, Building2, Repeat, AlertTriangle, CheckCircle2,
  Coins, Copy, Trash2, Search, Eye
} from "lucide-react";

const ReconciliationPage = () => {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Duplicates state
  const [duplicates, setDuplicates] = useState(null);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [selectedDuplicates, setSelectedDuplicates] = useState(new Set());
  const [removingDuplicates, setRemovingDuplicates] = useState(false);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const res = await apiService.previewReconciliation();
      setPreview(res.data);
    } catch (error) {
      console.error("Failed to fetch preview:", error);
      toast.error("Failed to load reconciliation preview");
    } finally {
      setLoading(false);
    }
  };

  const fetchDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const res = await apiService.findDuplicates();
      setDuplicates(res.data);
      setSelectedDuplicates(new Set());
      setActiveTab("duplicates");
    } catch (error) {
      console.error("Failed to find duplicates:", error);
      toast.error("Failed to analyze duplicates");
    } finally {
      setLoadingDuplicates(false);
    }
  };

  useEffect(() => {
    fetchPreview();
  }, []);

  const handleRunReconciliation = async () => {
    setRunning(true);
    try {
      const res = await apiService.runReconciliation();
      toast.success(`Reconciled ${res.data.total_reconciled} transactions`);
      fetchPreview();
    } catch (error) {
      toast.error("Reconciliation failed");
    } finally {
      setRunning(false);
    }
  };

  const toggleDuplicateSelection = (txId) => {
    setSelectedDuplicates(prev => {
      const next = new Set(prev);
      if (next.has(txId)) {
        next.delete(txId);
      } else {
        next.add(txId);
      }
      return next;
    });
  };

  const selectAllDuplicates = () => {
    if (!duplicates?.duplicate_groups) return;
    const allDuplicateIds = new Set();
    duplicates.duplicate_groups.forEach(group => {
      group.duplicates.forEach(dup => allDuplicateIds.add(dup.id));
    });
    setSelectedDuplicates(allDuplicateIds);
  };

  const handleRemoveSelectedDuplicates = async () => {
    if (selectedDuplicates.size === 0) return;
    if (!window.confirm(`Remove ${selectedDuplicates.size} duplicate(s)?`)) return;
    
    setRemovingDuplicates(true);
    try {
      const res = await apiService.removeDuplicates(Array.from(selectedDuplicates));
      toast.success(`Removed ${res.data.removed_count} duplicates`);
      fetchDuplicates();
      fetchPreview();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove");
    } finally {
      setRemovingDuplicates(false);
    }
  };

  const handleRemoveAllDuplicates = async () => {
    if (!window.confirm("Remove ALL duplicates? This cannot be undone.")) return;
    
    setRemovingDuplicates(true);
    try {
      const res = await apiService.removeAllDuplicates();
      toast.success(res.data.message);
      fetchDuplicates();
      fetchPreview();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove");
    } finally {
      setRemovingDuplicates(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatAmount = (amount, direction) => {
    const sign = direction === "income" ? "+" : "-";
    return `${sign}${Math.abs(amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  };

  const totalMatches = (preview?.summary?.internal_transfers_count || 0) +
    (preview?.summary?.currency_conversions_count || 0) +
    (preview?.summary?.round_trips_count || 0);

  if (loading && !preview) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reconciliation-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Transaction Reconciliation</h1>
          <p className="text-sm text-slate-500">Match internal transfers, currency conversions & find duplicates</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDuplicates}
            disabled={loadingDuplicates}
            className="text-orange-600 border-orange-200 hover:bg-orange-50"
          >
            {loadingDuplicates ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
            Find Duplicates
          </Button>
          <Button variant="outline" size="sm" onClick={fetchPreview} disabled={loading}>
            <Eye className="w-4 h-4 mr-1" />
            Preview
          </Button>
          <Button variant="outline" size="sm" onClick={fetchPreview} disabled={loading} className="px-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            size="sm"
            onClick={handleRunReconciliation}
            disabled={running || totalMatches === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            Apply ({totalMatches})
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Analyzed</p>
                <p className="text-2xl font-bold text-slate-900">{preview?.summary?.total_transactions_analyzed || 0}</p>
              </div>
              <div className="p-2 rounded-lg bg-slate-100">
                <Building2 className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Transfers</p>
                <p className="text-2xl font-bold text-blue-600">{preview?.summary?.internal_transfers_count || 0}</p>
                <p className="text-[10px] text-slate-500">ING ↔ Wise</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100">
                <ArrowLeftRight className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Conversions</p>
                <p className="text-2xl font-bold text-amber-600">{preview?.summary?.currency_conversions_count || 0}</p>
                <p className="text-[10px] text-slate-500">EUR ↔ USD</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-100">
                <Coins className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-violet-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Round-Trips</p>
                <p className="text-2xl font-bold text-violet-600">{preview?.summary?.round_trips_count || 0}</p>
                <p className="text-[10px] text-slate-500">Out & back</p>
              </div>
              <div className="p-2 rounded-lg bg-violet-100">
                <Repeat className="w-5 h-5 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 h-9">
          <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
          <TabsTrigger value="transfers" className="text-xs">
            Transfers {preview?.summary?.internal_transfers_count > 0 && `(${preview.summary.internal_transfers_count})`}
          </TabsTrigger>
          <TabsTrigger value="conversions" className="text-xs">
            Conversions {preview?.summary?.currency_conversions_count > 0 && `(${preview.summary.currency_conversions_count})`}
          </TabsTrigger>
          <TabsTrigger value="roundtrips" className="text-xs">
            Round-Trips {preview?.summary?.round_trips_count > 0 && `(${preview.summary.round_trips_count})`}
          </TabsTrigger>
          <TabsTrigger value="duplicates" className="text-xs">
            Duplicates {duplicates?.summary?.total_duplicates > 0 && (
              <Badge className="ml-1 h-4 px-1 text-[10px] bg-red-500">{duplicates.summary.total_duplicates}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">Reconciliation Rules</p>
                  <ul className="mt-1 text-blue-700 space-y-0.5">
                    <li>• <strong>ING ↔ Wise transfers</strong> are internal (Edomatch B.V.)</li>
                    <li>• <strong>Currency conversions</strong> = same bank, same reference (BALANCE-xxx), different currencies</li>
                    <li>• <strong>Round-trips</strong> = same amount, same counterparty, opposite directions within 30 days</li>
                    <li>• <strong>Duplicates</strong> = same date, amount, direction, counterparty, bank, currency</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Internal Transfers Tab */}
        <TabsContent value="transfers" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <ArrowLeftRight className="w-5 h-5 text-blue-600" />
                <CardTitle className="text-base">Internal Transfers (ING ↔ Wise)</CardTitle>
              </div>
              <CardDescription>Transfers between Edomatch B.V. bank accounts - not counted as expenses</CardDescription>
            </CardHeader>
            <CardContent>
              {preview?.internal_transfers?.length > 0 ? (
                <ScrollArea className="h-[350px]">
                  <div className="space-y-3">
                    {preview.internal_transfers.map((match, idx) => (
                      <div key={idx} className="border rounded-lg p-3 bg-blue-50/50">
                        <div className="flex items-center gap-3">
                          {/* Transaction 1 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="bg-white text-xs">{match.tx1.bank}</Badge>
                              <span className="text-xs text-slate-500">{formatDate(match.tx1.date)}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-900 mt-1 truncate">
                              {match.tx1.counterparty || match.tx1.description?.substring(0, 40)}
                            </p>
                            <p className={`text-sm font-semibold ${match.tx1.direction === 'income' ? 'text-emerald-600' : 'text-red-500'}`}>
                              {formatAmount(match.tx1.amount, match.tx1.direction)} {match.tx1.currency}
                            </p>
                          </div>

                          <ArrowRight className="w-5 h-5 text-blue-400 flex-shrink-0" />

                          {/* Transaction 2 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="bg-white text-xs">{match.tx2.bank}</Badge>
                              <span className="text-xs text-slate-500">{formatDate(match.tx2.date)}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-900 mt-1 truncate">
                              {match.tx2.counterparty || match.tx2.description?.substring(0, 40)}
                            </p>
                            <p className={`text-sm font-semibold ${match.tx2.direction === 'income' ? 'text-emerald-600' : 'text-red-500'}`}>
                              {formatAmount(match.tx2.amount, match.tx2.direction)} {match.tx2.currency}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300 mb-2" />
                  <p>No pending internal transfers to match</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Currency Conversions Tab */}
        <TabsContent value="conversions" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-amber-600" />
                <CardTitle className="text-base">Currency Conversions</CardTitle>
              </div>
              <CardDescription>Internal currency exchanges within the same bank (e.g., EUR → USD on Wise) - not real income/expense</CardDescription>
            </CardHeader>
            <CardContent>
              {preview?.currency_conversions?.length > 0 ? (
                <ScrollArea className="h-[350px]">
                  <div className="space-y-3">
                    {preview.currency_conversions.map((match, idx) => (
                      <div key={idx} className="border rounded-lg p-3 bg-amber-50/50">
                        <div className="flex items-center gap-3">
                          {/* Transaction 1 - Source */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">OUT</Badge>
                              <Badge variant="outline" className="bg-white text-xs">{match.tx1.currency}</Badge>
                              <span className="text-xs text-slate-500">{formatDate(match.tx1.date)}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-900 mt-1 truncate">
                              {match.tx1.counterparty || match.tx1.description?.substring(0, 40) || "Currency Exchange"}
                            </p>
                            <p className="text-sm font-semibold text-red-500">
                              -{Math.abs(match.tx1.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })} {match.tx1.currency}
                            </p>
                          </div>

                          <div className="flex flex-col items-center flex-shrink-0">
                            <ArrowRight className="w-5 h-5 text-amber-500" />
                            <span className="text-[10px] text-amber-600 font-medium">Convert</span>
                          </div>

                          {/* Transaction 2 - Target */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">IN</Badge>
                              <Badge variant="outline" className="bg-white text-xs">{match.tx2.currency}</Badge>
                              <span className="text-xs text-slate-500">{formatDate(match.tx2.date)}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-900 mt-1 truncate">
                              {match.tx2.counterparty || match.tx2.description?.substring(0, 40) || "Currency Exchange"}
                            </p>
                            <p className="text-sm font-semibold text-emerald-600">
                              +{Math.abs(match.tx2.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })} {match.tx2.currency}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300 mb-2" />
                  <p>No currency conversions to reconcile</p>
                  <p className="text-xs mt-1 text-slate-400">Import Wise transactions to see currency conversions</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Round-Trips Tab */}
        <TabsContent value="roundtrips" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Repeat className="w-5 h-5 text-violet-600" />
                <CardTitle className="text-base">Round-Trip Transactions</CardTitle>
              </div>
              <CardDescription>Amounts that went out and came back with the same counterparty - cancel each other out</CardDescription>
            </CardHeader>
            <CardContent>
              {preview?.round_trips?.length > 0 ? (
                <ScrollArea className="h-[350px]">
                  <div className="space-y-3">
                    {preview.round_trips.map((match, idx) => (
                      <div key={idx} className="border rounded-lg p-3 bg-violet-50/50">
                        <div className="flex items-center gap-3">
                          {/* Transaction 1 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className={`text-xs ${match.tx1.direction === 'expense' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                                {match.tx1.direction === 'expense' ? 'OUT' : 'IN'}
                              </Badge>
                              <span className="text-xs text-slate-500">{formatDate(match.tx1.date)}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-900 mt-1 truncate">{match.tx1.counterparty}</p>
                            <p className={`text-sm font-semibold ${match.tx1.direction === 'income' ? 'text-emerald-600' : 'text-red-500'}`}>
                              {formatAmount(match.tx1.amount, match.tx1.direction)} {match.tx1.currency}
                            </p>
                          </div>

                          <Repeat className="w-5 h-5 text-violet-400 flex-shrink-0" />

                          {/* Transaction 2 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className={`text-xs ${match.tx2.direction === 'expense' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                                {match.tx2.direction === 'expense' ? 'OUT' : 'IN'}
                              </Badge>
                              <span className="text-xs text-slate-500">{formatDate(match.tx2.date)}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-900 mt-1 truncate">{match.tx2.counterparty}</p>
                            <p className={`text-sm font-semibold ${match.tx2.direction === 'income' ? 'text-emerald-600' : 'text-red-500'}`}>
                              {formatAmount(match.tx2.amount, match.tx2.direction)} {match.tx2.currency}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300 mb-2" />
                  <p>No pending round-trip transactions to match</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Duplicates Tab */}
        <TabsContent value="duplicates" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Copy className="w-5 h-5 text-orange-600" />
                  <CardTitle className="text-base">Duplicate Transactions</CardTitle>
                  {duplicates?.summary?.total_duplicates > 0 && (
                    <Badge className="bg-orange-100 text-orange-700">{duplicates.summary.total_duplicates} found</Badge>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={fetchDuplicates} disabled={loadingDuplicates}>
                  {loadingDuplicates ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              <CardDescription>Same date, amount, direction, counterparty, bank, and currency</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingDuplicates ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                </div>
              ) : duplicates?.duplicate_groups?.length > 0 ? (
                <div className="space-y-3">
                  {/* Action Bar */}
                  <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-orange-700">{selectedDuplicates.size} selected</span>
                      <Button variant="link" size="sm" className="h-auto p-0 text-orange-600" onClick={selectAllDuplicates}>
                        Select all
                      </Button>
                      <Button variant="link" size="sm" className="h-auto p-0 text-slate-500" onClick={() => setSelectedDuplicates(new Set())}>
                        Clear
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-red-600 border-red-200"
                        onClick={handleRemoveSelectedDuplicates}
                        disabled={selectedDuplicates.size === 0 || removingDuplicates}
                      >
                        {removingDuplicates ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                        Remove Selected
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 bg-red-600 hover:bg-red-700"
                        onClick={handleRemoveAllDuplicates}
                        disabled={removingDuplicates}
                      >
                        Remove All
                      </Button>
                    </div>
                  </div>

                  {/* Duplicate Groups */}
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {duplicates.duplicate_groups.map((group, idx) => (
                        <div key={idx} className="border rounded-lg p-3 bg-white">
                          {/* Group Header */}
                          <div className="flex items-center gap-2 mb-2 text-xs text-slate-500 flex-wrap">
                            <Badge variant="outline">{group.count}x</Badge>
                            <span>{formatDate(group.key.date)}</span>
                            <span>·</span>
                            <span>{group.key.bank}</span>
                            <span>·</span>
                            <span className={group.key.direction === "income" ? "text-emerald-600" : "text-red-500"}>
                              {group.key.direction === "income" ? "+" : "-"}{group.key.amount} {group.key.currency}
                            </span>
                          </div>

                          {/* Original */}
                          <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded mb-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{group.original.counterparty_name || "Unknown"}</p>
                            </div>
                            <Badge className="bg-emerald-600 text-white text-xs">Keep</Badge>
                          </div>

                          {/* Duplicates */}
                          {group.duplicates.map((dup) => (
                            <div
                              key={dup.id}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors mb-1 ${
                                selectedDuplicates.has(dup.id) ? "bg-red-50 border border-red-200" : "bg-slate-50 hover:bg-slate-100"
                              }`}
                              onClick={() => toggleDuplicateSelection(dup.id)}
                            >
                              <Checkbox
                                checked={selectedDuplicates.has(dup.id)}
                                className="data-[state=checked]:bg-red-500"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm truncate">{dup.counterparty_name || "Unknown"}</p>
                              </div>
                              <Badge variant="outline" className="text-xs text-red-600 border-red-200">Duplicate</Badge>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              ) : duplicates ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300 mb-2" />
                  <p>No duplicates found</p>
                  <p className="text-xs mt-1 text-slate-400">All transactions are unique</p>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Search className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  <p>Click Scan to check for duplicates</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReconciliationPage;
